import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EquipmentEvidenceDialog, WorkerPickerField } from "@/components/equipment/EquipmentDialogs";
import { EquipmentInventoryTable } from "@/components/equipment/EquipmentInventoryTable";
import { EquipmentRejectionBanner } from "@/components/equipment/EquipmentRejectionBanner";
import { useRole } from "@/contexts/RoleContext";
import {
  createEquipmentAsset,
  EquipmentApiError,
  initiateAssetHandover,
  initiateAssetReturn,
  listEquipmentAssets,
  retireEquipmentAsset,
  updateEquipmentAsset,
} from "@/lib/equipmentApi";
import { CLEANING_STAFF_ROLES, isActiveMembership } from "@/lib/cleaningStaffRoles";
import { supabase } from "@/lib/supabase";
import type { EquipmentAsset } from "@/types/equipment";

export function EquipmentInventoryPanel() {
  const { currentOrgId, currentRole } = useRole();
  const canEdit = currentRole === "owner" || currentRole === "coordinator";
  const [assets, setAssets] = useState<EquipmentAsset[]>([]);
  const [workers, setWorkers] = useState<{ value: string; label: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [serial, setSerial] = useState("");
  const [editAsset, setEditAsset] = useState<EquipmentAsset | null>(null);
  const [editName, setEditName] = useState("");
  const [editSerial, setEditSerial] = useState("");
  const [handoverAsset, setHandoverAsset] = useState<EquipmentAsset | null>(null);
  const [workerId, setWorkerId] = useState("");
  const [returnAsset, setReturnAsset] = useState<EquipmentAsset | null>(null);

  const load = useCallback(async () => {
    if (!currentOrgId) return;
    try {
      const rows = await listEquipmentAssets(currentOrgId);
      setAssets(rows.filter((item) => item.status !== "retired"));
    } catch (error) {
      console.error("[EquipmentInventoryPanel]", error);
      toast.error(error instanceof EquipmentApiError ? error.message : "Nie udało się pobrać stanu sprzętu.");
    }
  }, [currentOrgId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!currentOrgId) return;
    void (async () => {
      const { data: members, error } = await supabase
        .from("memberships")
        .select("user_id, role, is_active")
        .eq("org_id", currentOrgId)
        .in("role", [...CLEANING_STAFF_ROLES]);
      if (error) {
        console.error("[EquipmentInventoryPanel] memberships", error);
        return;
      }
      const ids = [...new Set((members ?? []).filter((m) => isActiveMembership(m.is_active)).map((m) => m.user_id))];
      if (ids.length === 0) {
        setWorkers([]);
        return;
      }
      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .in("id", ids);
      if (profileError) {
        console.error("[EquipmentInventoryPanel] profiles", profileError);
        return;
      }
      setWorkers(
        (profiles ?? []).map((p) => ({
          value: p.id,
          label: p.full_name || p.email || "Pracownik",
        })),
      );
    })();
  }, [currentOrgId]);

  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      setSaving(true);
      await action();
      toast.success(success);
      await load();
    } catch (error) {
      console.error("[EquipmentInventoryPanel]", error);
      toast.error(error instanceof EquipmentApiError ? error.message : "Nie udało się zapisać.");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (asset: EquipmentAsset) => {
    setEditAsset(asset);
    setEditName(asset.name);
    setEditSerial(asset.serial_number ?? "");
  };

  if (!currentOrgId) return null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Stan sprzętu</CardTitle>
              <CardDescription>Unikalne narzędzia firmy — kosiarki, piły i inne egzemplarze.</CardDescription>
            </div>
            {canEdit && (
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Dodaj narzędzie
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {assets.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">Brak narzędzi na stanie.</p>
          ) : (
            <EquipmentInventoryTable
              assets={assets}
              canEdit={canEdit}
              saving={saving}
              onEdit={openEdit}
              onHandover={setHandoverAsset}
              onReturn={setReturnAsset}
              onRetire={(asset) => void run(() => retireEquipmentAsset(asset.id), "Narzędzie wycofane")}
            />
          )}
        </CardContent>

        <EquipmentEvidenceDialog
          open={addOpen}
          title="Dodaj narzędzie"
          description="Nowy egzemplarz trafia na stan firmy jako wolny."
          submitLabel="Dodaj"
          saving={saving}
          onOpenChange={(open) => {
            setAddOpen(open);
            if (!open) {
              setName("");
              setSerial("");
            }
          }}
          hidePhotos
          extra={
            <>
              <div className="space-y-2">
                <Label htmlFor="asset-name">Nazwa</Label>
                <Input
                  id="asset-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="np. Kosiarka Honda"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="asset-serial">Numer seryjny (opcjonalnie)</Label>
                <Input id="asset-serial" value={serial} onChange={(e) => setSerial(e.target.value)} />
              </div>
            </>
          }
          onSubmit={(notes) => {
            if (!name.trim()) {
              toast.error("Podaj nazwę narzędzia.");
              return;
            }
            void run(async () => {
              await createEquipmentAsset({
                orgId: currentOrgId,
                name,
                serialNumber: serial,
                notes,
              });
              setAddOpen(false);
              setName("");
              setSerial("");
            }, "Narzędzie dodane na stan");
          }}
        />

        <EquipmentEvidenceDialog
          open={Boolean(editAsset)}
          title="Edytuj narzędzie"
          description="Zmień nazwę, numer seryjny albo opis stanu."
          submitLabel="Zapisz"
          saving={saving}
          hidePhotos
          initialNotes={editAsset?.notes ?? ""}
          extra={
            <>
              <div className="space-y-2">
                <Label htmlFor="edit-asset-name">Nazwa</Label>
                <Input id="edit-asset-name" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-asset-serial">Numer seryjny (opcjonalnie)</Label>
                <Input id="edit-asset-serial" value={editSerial} onChange={(e) => setEditSerial(e.target.value)} />
              </div>
            </>
          }
          onOpenChange={(open) => {
            if (!open) setEditAsset(null);
          }}
          onSubmit={(notes) => {
            if (!editAsset) return;
            if (!editName.trim()) {
              toast.error("Podaj nazwę narzędzia.");
              return;
            }
            void run(async () => {
              await updateEquipmentAsset({
                id: editAsset.id,
                name: editName,
                serialNumber: editSerial,
                notes,
              });
              setEditAsset(null);
            }, "Dane narzędzia zapisane");
          }}
        />

        <EquipmentEvidenceDialog
          open={Boolean(handoverAsset)}
          title="Przekaż narzędzie"
          description="Pracownik potwierdzi odbiór w swojej aplikacji. Stan możesz poprawić przed wysłaniem."
          submitLabel="Przekaż"
          saving={saving}
          initialNotes={handoverAsset?.notes ?? ""}
          extra={<WorkerPickerField options={workers} value={workerId} onChange={setWorkerId} />}
          onOpenChange={(open) => {
            if (!open) {
              setHandoverAsset(null);
              setWorkerId("");
            }
          }}
          onSubmit={(notes, files) => {
            if (!handoverAsset || !workerId) {
              toast.error("Wybierz pracownika.");
              return;
            }
            void run(async () => {
              await initiateAssetHandover({
                orgId: currentOrgId,
                workerId,
                asset: handoverAsset,
                conditionNotes: notes,
                photos: files,
              });
              setHandoverAsset(null);
              setWorkerId("");
            }, "Protokół przekazania wysłany");
          }}
        />

        <EquipmentEvidenceDialog
          open={Boolean(returnAsset)}
          title="Zdaj narzędzie"
          description="Pracownik albo koordynator musi potwierdzić zwrot."
          submitLabel="Zdaj"
          saving={saving}
          initialNotes={returnAsset?.notes ?? ""}
          onOpenChange={(open) => {
            if (!open) setReturnAsset(null);
          }}
          onSubmit={(notes, files) => {
            if (!returnAsset) return;
            void run(async () => {
              await initiateAssetReturn({ asset: returnAsset, conditionNotes: notes, photos: files });
              setReturnAsset(null);
            }, "Protokół zwrotu wysłany");
          }}
        />
      </Card>
      {canEdit && <EquipmentRejectionBanner orgId={currentOrgId} />}
    </div>
  );
}
